// YERİ: src/app/domains/website/core/tag-loader.service.ts
//
// Injects the storefront's third-party tags — GA4 (gtag.js), Google Ads (same
// gtag.js) and Meta Pixel — AFTER consent. Only TrackingConsentStore calls it.
//
// CSP: the site runs `script-src 'nonce-…' 'strict-dynamic'`. Scripts created
// with document.createElement by our already-trusted bundle inherit trust, so no
// inline <script>, no document.write and no inline event attributes are used —
// the gtag/fbq stubs are defined here in TypeScript.
//
// Google Consent Mode v2: a "default: denied" is queued before gtag.js arrives,
// then an "update" with the visitor's actual choice.

import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

/** GET /api/web/tracking-config — public ids only. */
export interface TrackingConfig {
  ga4MeasurementId: string;
  metaPixelId: string;
  googleAdsTagId: string;
  consentEnabled: boolean;
}

export interface TagConsent {
  analytics: boolean;
  ads: boolean;
}

type CommandFn = (...args: unknown[]) => void;

interface FbqFn extends CommandFn {
  callMethod?: CommandFn;
  queue: unknown[];
  push: FbqFn;
  loaded: boolean;
  version: string;
}

/** Window fields the vendor snippets expect. Local type — no global augmentation. */
interface TagWindow {
  dataLayer?: unknown[];
  gtag?: CommandFn;
  fbq?: FbqFn;
  _fbq?: FbqFn;
}

const GA4_ID = /^G-[A-Z0-9]{4,20}$/;
const ADS_ID = /^AW-\d{5,15}$/;
const PIXEL_ID = /^\d{3,20}$/;

@Injectable({ providedIn: 'root' })
export class TagLoader {
  private doc = inject(DOCUMENT);
  private router = inject(Router);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private gtagLoaded = false;
  private ga4Configured = false;
  private adsConfigured = false;
  private pixelLoaded = false;

  /** Loads what the consent allows. Safe to call more than once. */
  load(config: TrackingConfig, consent: TagConsent): void {
    if (!this.isBrowser) return;

    const ga4 = GA4_ID.test(config.ga4MeasurementId ?? '') ? config.ga4MeasurementId : '';
    const ads = ADS_ID.test(config.googleAdsTagId ?? '') ? config.googleAdsTagId : '';
    const pixel = PIXEL_ID.test(config.metaPixelId ?? '') ? config.metaPixelId : '';

    const wantGa4 = consent.analytics && !!ga4;
    const wantAds = consent.ads && !!ads;

    if (wantGa4 || wantAds) {
      const gtag = this.ensureGtag(wantGa4 ? ga4 : ads);
      gtag('consent', 'update', {
        analytics_storage: consent.analytics ? 'granted' : 'denied',
        ad_storage: consent.ads ? 'granted' : 'denied',
        ad_user_data: consent.ads ? 'granted' : 'denied',
        ad_personalization: consent.ads ? 'granted' : 'denied',
      });
      if (wantGa4 && !this.ga4Configured) {
        this.ga4Configured = true;
        gtag('config', ga4, { anonymize_ip: true });
      }
      if (wantAds && !this.adsConfigured) {
        this.adsConfigured = true;
        gtag('config', ads);
      }
    }

    if (consent.ads && pixel) this.loadPixel(pixel);
  }

  // ── gtag.js ───────────────────────────────────────────────────────────────

  private ensureGtag(primaryId: string): CommandFn {
    const w = this.win();
    if (!this.gtagLoaded) {
      this.gtagLoaded = true;
      w.dataLayer = w.dataLayer ?? [];
      const dataLayer = w.dataLayer;
      // gtag.js only understands `arguments` objects, not arrays.
      w.gtag = function gtag() {
        // eslint-disable-next-line prefer-rest-params
        dataLayer.push(arguments);
      };

      // Consent Mode v2 defaults — must precede config and the library itself.
      w.gtag('consent', 'default', {
        analytics_storage: 'denied',
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        wait_for_update: 500,
      });
      w.gtag('js', new Date());

      this.injectScript(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(primaryId)}`);
    }
    return w.gtag!;
  }

  // ── Meta Pixel ────────────────────────────────────────────────────────────

  private loadPixel(pixelId: string): void {
    if (this.pixelLoaded) return;
    this.pixelLoaded = true;

    const w = this.win();
    if (!w.fbq) {
      // Same queueing stub as Meta's snippet, written as code (no inline script).
      const stub: FbqFn = function () {
        // eslint-disable-next-line prefer-rest-params
        const args = arguments;
        if (stub.callMethod) stub.callMethod.apply(stub, Array.from(args));
        else stub.queue.push(args);
      } as unknown as FbqFn;
      stub.push = stub;
      stub.loaded = true;
      stub.version = '2.0';
      stub.queue = [];
      w.fbq = stub;
      w._fbq = w._fbq ?? stub;
      this.injectScript('https://connect.facebook.net/en_US/fbevents.js');
    }

    const fbq = w.fbq!;
    fbq('consent', 'grant');
    fbq('init', pixelId);
    fbq('track', 'PageView');

    // SPA navigation: the pixel does not see History API changes by itself.
    // (GA4's enhanced measurement already tracks history-based page views.)
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => w.fbq?.('track', 'PageView'));
  }

  // ── helpers ───────────────────────────────────────────────────────────────

  private injectScript(src: string): void {
    const script = this.doc.createElement('script');
    script.async = true;
    script.src = src;
    this.doc.head.appendChild(script);
  }

  private win(): TagWindow {
    return this.doc.defaultView as unknown as TagWindow;
  }
}
